/*    
<summary>
  This functional component "AddProductManagementForm" provides form to add new user or update existing user.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import {
  Field,
  Form,
  Formik
} from "formik";
import _ from "lodash";
import { observer } from "mobx-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { DATE_FORMAT, integerRegex } from "../../../../constants/constants";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import { IOption } from "../../../../models/ICommon";
import AutoCalculateTax from "../../../../shared-components/auto-tax-calculation/useAutoCalculateTax";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikDateFieldMUI from "../../../../shared-components/FormikFormHandlers/FormikDateFieldMUI";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputRegexCheckFormattedNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputRegexCheckFormattedNumber";
import FormikFormOnChangeSelect from "../../../../shared-components/FormikFormHandlers/FormikFormOnChangeSelect";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import FormikFormTextArea from "../../../../shared-components/FormikFormHandlers/FormikFormTextArea";
import FormikMultiSelect from "../../../../shared-components/FormikFormHandlers/FormikMultiSelect";
import {
  AddOptionMenuSchema,
  EditOptionMenuSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getOptionMenuLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  treatmentSummary: IOption[];
  timeOptions: IOption[];
  taxOptions: IOption[];
  registrationTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  deleteActionLoading: boolean;
  viewOnly: boolean;
}

const AddStoreOptionMenuForm = (props: IProps) => {
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    deleteActionLoading,
    viewOnly,
    treatmentSummary,
  } = props;

  const { treatmentManagementStore } = useStore();
  const { getTreatmentSummary } = treatmentManagementStore;

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  const [selectedBusinessType, setselectedBusinessType] = useState(-1);

  useEffect(() => {
    const businessTypeId =
      selectedBusinessType > -1
        ? selectedBusinessType
        : props.initialValues?.BusinessTypeId;

    if (!businessTypeId || businessTypeId === -1) {
      // setTreatmentOptions([]);
      return;
    }
  }, [selectedBusinessType, props.initialValues]);

  useEffect(() => {
    const businessTypeId =
      selectedBusinessType > -1
        ? selectedBusinessType
        : props.initialValues?.BusinessTypeId;

    if (
      (!businessTypeId || businessTypeId === -1) &&
      !props.initialValues?.Treatment?.length
    ) {
      // setTreatmentOptions([]);
      return;
    }
  }, [
    props.initialValues?.BusinessTypeId,
    props.initialValues?.Treatment?.length,
    selectedBusinessType,
  ]);

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={deleteActionLoading}
          title={formatMessage("delete_confirmation")}
          message={formatMessage(
            "are_you_sure_you_want_to_delete_this_option?"
          )}
        />
      )}

      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="option_menu_registration" />
            ) : (
              <FormattedMessage id="option_menu_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.getOptionMenuLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            ...props.initialValues,
          }}
          enableReinitialize={true}
          validationSchema={
            props.id === -1 ? AddOptionMenuSchema : EditOptionMenuSchema
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            const treatmentArray = Array.isArray(values.Treatment)
              ? values.Treatment
              : [values.Treatment];
            const formattedTreatment = treatmentArray.map(
              (treatmentId: number) => ({
                TreatmentId: treatmentId,
              })
            );

            const isTreatmentchanged = !_.isEqual(
              _.sortBy(props.initialValues.Treatment || []),
              _.sortBy(treatmentArray)
            );
            let addObj = {
              RegistrationType: values.RegistrationType,
              BusinessTypeId: values.BusinessTypeId,
              Treatment: formattedTreatment,
              OptionName: values.OptionName,
              FeeExcludingTax:
                values?.FeeExcludingTax == ""
                  ? 0
                  : Number(values.FeeExcludingTax),
              TaxId: values.TaxId,
              TaxAmount: values.TaxAmount || 0,
              TaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.TaxId
                )?.value ?? 0,
              FeeIncludingTax: Number(values.FeeIncludingTax),
              ReservationSite: values.ReservationSite,
              Status: values.Status,
              StartDate: values.StartDate
                ? new Date(values.StartDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.StartDate.$d).format(DATE_FORMAT)
                  : values.StartDate
                : null,
              EndDate: values.EndDate
                ? new Date(values.EndDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.EndDate.$d).format(DATE_FORMAT)
                  : values.EndDate
                : null,
              TimeId: values.TimeId,
              TimeValue:
                props.timeOptions.find(
                  (item: IOption) => item.id == values.TimeId
                )?.value ?? 0,
              Remarks: values?.Remarks,
              Description: values?.Description,
            };

            let isStatusChanged = false;
            if (props.initialValues.Status !== values.Status) isStatusChanged = true;
            let updateObj = {
              RegistrationType: values.RegistrationType,
              BusinessTypeId: values.BusinessTypeId,
              Treatment: formattedTreatment,
              OptionName: values.OptionName,
              FeeExcludingTax:
                values?.FeeExcludingTax == ""
                  ? 0
                  : Number(values.FeeExcludingTax),
              TaxId: values.TaxId,
              TaxValue:
                props.taxOptions.find(
                  (item: IOption) => item.id == values.TaxId
                )?.value ?? 0,
              TaxAmount: values.TaxAmount || 0,
              FeeIncludingTax: Number(toHalfWidth(values.FeeIncludingTax)),
              ReservationSite: values.ReservationSite,
              Status: values.Status,
              StartDate: values.StartDate
                ? new Date(values.StartDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.StartDate.$d).format(DATE_FORMAT)
                  : values.StartDate
                : null,
              EndDate: values.EndDate
                ? new Date(values.EndDate?.$d).toString() !== "Invalid Date"
                  ? moment(values.EndDate.$d).format(DATE_FORMAT)
                  : values.EndDate
                : null,
              TimeId: values.TimeId,
              TimeValue:
                props.timeOptions.find(
                  (item: IOption) => item.id == values.TimeId
                )?.value ?? 0,
              Remarks: values?.Remarks,
              Description: values?.Description,
              IsTreatmentChanges: isTreatmentchanged,
              IsStatusChanged: isStatusChanged
            };
            props.submitHandler(props.id > 0 ? updateObj : addObj);
          }}
        >
          {({ values, setFieldValue }) => {
            const isHeadquarterOption = values.RegistrationType === 0 && getUserTypeFromLocalStorage() === userTypeEnum[userTypeEnum.Store];
            const handleClick = async (
              event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
              fieldName: string
            ) => {
              const field = fieldName;

              if (field === "FeeExcludingTaxIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              } else if (field === "FeeExcludingTaxDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              }
              else if (field === "TaxAmountIncreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) - 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) + 1); // Uncheck the other checkbox
              } else if (field === "TaxAmountDecreaseBtn") {
                setFieldValue("FeeExcludingTax", parseInt(values.FeeExcludingTax) + 1);
                setFieldValue("TaxAmount", parseInt(values.TaxAmount) - 1); // Uncheck the other checkbox
              }
            };
            return (
              <Form className="user-form">
                <AutoCalculateTax
                  id={props.id}
                  taxOptions={props.taxOptions}
                  feeExcludingTaxField="FeeExcludingTax"
                  taxIdField="TaxId"
                  taxAmountField="TaxAmount"
                  feeIncludingTaxField="FeeIncludingTax"
                />
                {props.getOptionMenuLoading && (
                  <FormLoader loading={props.getOptionMenuLoading} />
                )}
                <Modal.Body>
                  {/* Row 1: Registration Type, Business Type */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="registration_type"
                      />
                      {props.id > 0 ? (
                        <Field
                          name="RegistrationTypeName"
                          as={FormikFormInput}
                          disabled={true}
                          className="form-control"
                          type="text"
                          validcharacter={["+", "(", ")", "-", "_", "&"]}
                        />
                      ) : (
                        <Field
                          name="RegistrationType"
                          as={FormikFormSelect}
                          options={props.registrationTypeOptions}
                          disabled={true}
                        />
                      )}
                    </div>
                    {getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.Store] && (
                      <div className="col-sm-6">
                        <Label
                          className="label-style"
                          required={true}
                          label="business_type"
                        />
                        <Field
                          name="BusinessTypeId"
                          as={FormikFormOnChangeSelect}
                          setState={setselectedBusinessType}
                          options={props.businessTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() ===
                            userTypeEnum[userTypeEnum.Store]
                              ? true
                              : viewOnly
                          }
                        />
                      </div>
                    )}
                  </div>

                  {/* Row 2: Treatment, Reservation Site (Radio) */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="treatment"
                      />
                      <FormikMultiSelect
                        name="Treatment"
                        options={getTreatmentSummary.map((item) => {
                          return {
                            label: item?.value ?? "",
                            value: item?.id,
                          };
                        })}
                        className="custom-select-class"
                        isDisabled={isHeadquarterOption}
                      />
                    </div>

                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="reservation_site"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="ReservationSite"
                            type="radio"
                            value="1"
                            id="use"
                            // disabled={
                            //   values.RegistrationType === 0 &&
                            //   getUserTypeFromLocalStorage() ===
                            //     userTypeEnum[userTypeEnum.Store]
                            // }
                          />
                          <label className="form-check-label" htmlFor="use">
                            {formatMessage("use")}
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="ReservationSite"
                            type="radio"
                            value="0"
                            id="dont_use"
                            // disabled={
                            //   values.RegistrationType === 0 &&
                            //   getUserTypeFromLocalStorage() ===
                            //     userTypeEnum[userTypeEnum.Store]
                            // }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="dont_use"
                          >
                            {formatMessage("not_use")}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 3: Option Name, Time */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="option_name"
                      />
                      <Field
                        name="OptionName"
                        type="text"
                        className="form-control"
                        as={FormikFormInput}
                        // validcharacter={["+", "(", ")", "-", "_"]}
                        // validcharacter={[
                        //   "+",
                        //   "-",
                        //   "[",
                        //   "]",
                        //   "|",
                        //   "*",
                        //   "&",
                        //   "/",
                        // ]}
                        validcharacter={[
                          "・",
                          "／",
                          "－",
                          "～",
                          "＋",
                          "（",
                          "）",
                          "［",
                          "］",
                          "※",
                          "：",
                          "★",
                          "☆",
                          "◎",
                          "○",
                          "●",
                          "◆",
                          "◇",
                          "■",
                          "□",
                          "→",
                          "⇒",
                          "♪",
                          "(",
                          ")",
                          "【",
                          "】",
                          "￥",
                          '"',
                          "＜",
                          "＞",
                          "！",
                          "？",
                          "↓",
                          "↑",
                          "←",
                          "．",
                          '"',
                          "゛",
                          "'",
                          "'",
                          '"',
                          '"',
                          "、",
                          "。",
                          "《",
                          "》",
                          "+",
                          "-",
                          "[",
                          "]",
                          "|",
                          "*",
                          "&",
                          "/",
                        ]}
                        placeholder="enter_option_name"
                        disabled={isHeadquarterOption}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="time"
                      />
                      <Field
                        name="TimeId"
                        className="form-control"
                        as={FormikFormSelect}
                        options={props.timeOptions}
                        // disabled={
                        //   values.RegistrationType === 0 &&
                        //   getUserTypeFromLocalStorage() ===
                        //     userTypeEnum[userTypeEnum.Store]
                        // }
                      />
                    </div>
                  </div>

                  {/* Row 4: Fee (Excluding Tax), Tax */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="fee(including_tax)"
                      />
                      <Field
                        name="FeeIncludingTax"
                        className="form-control"
                        type="text"
                        regex={integerRegex}
                        placeholder="enter_fee"
                        as={FormikFormInputRegexCheckFormattedNumber}
                        disabled={isHeadquarterOption}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={true}
                        label="tax"
                      />
                      <Field
                        name="TaxId"
                        as={FormikFormSelect}
                        options={props.taxOptions}
                        disabled={isHeadquarterOption}
                      />
                    </div>
                  </div>

                  {/* Row 5: Tax Amount, Fee (Including Tax) */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label className="label-style" label="tax_amount" />
                        <div className="input-group tax-group">
                        <Field
                          name="TaxAmount"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                      
                        <button
                        name="TaxAmountDecreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountDecreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={isHeadquarterOption ? isHeadquarterOption : (Math.round(parseFloat(values.FeeIncludingTax) - (parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100)))) - 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-dash-lg"></i>
                      </button>
                      <button
                        name="TaxAmountIncreaseBtn"
                        type="button"
                        onClick={(e) =>
                          handleClick(e, "TaxAmountIncreaseBtn")
                        }
                        className="btn btn-dark"
                        disabled={isHeadquarterOption ? isHeadquarterOption : (Math.round(parseFloat(values.FeeIncludingTax) - parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.TaxAmount) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                      >
                        <i className="bi bi-plus-lg"></i>
                      </button>
                      
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        label="fee(excluding_tax)"
                      />
                      <div className="input-group tax-group">
                        <Field
                          name="FeeExcludingTax"
                          className="form-control"
                          type="text"
                          disabled={true}
                          regex={integerRegex}
                          as={FormikFormInputRegexCheckFormattedNumber}
                        />
                        <button
                          name="FeeExcludingTaxDecreaseBtn"
                          className="btn btn-dark"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "FeeExcludingTaxDecreaseBtn")
                          }
                          disabled={isHeadquarterOption ? isHeadquarterOption : (Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) - 1 === values.FeeExcludingTax) || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-dash-lg"></i>
                        </button>
                        <button
                          name="FeeExcludingTaxIncreaseBtn"
                          className="btn btn-dark"
                          type="button"
                          onClick={(e) =>
                            handleClick(e, "FeeExcludingTaxIncreaseBtn")
                          }
                          disabled={isHeadquarterOption ? isHeadquarterOption : (Math.round(parseFloat(values.FeeIncludingTax) / (1 + (values.TaxValue / 100))) + 1 === values.FeeExcludingTax)  || (values.FeeExcludingTax == 0 || values.TaxAmount == 0)}
                        >
                          <i className="bi bi-plus-lg"></i>
                        </button>
                    </div>
                    </div>
                  </div>

                  {/* Row 6: Description */}
                  <div className="form-group mb-3">
                    <Label className="label-style" label="description" />
                    <Field
                      name="Description"
                      as={FormikFormTextArea}
                      className="form-control"
                      placeholder="enter_description"
                      disabled={
                        values.RegistrationType === 0 &&
                        getUserTypeFromLocalStorage() ===
                          userTypeEnum[userTypeEnum.Store]
                      }
                      validcharacter={[
                        "・",
                        "／",
                        "－",
                        "～",
                        "＋",
                        "（",
                        "）",
                        "［",
                        "］",
                        "※",
                        "：",
                        "★",
                        "☆",
                        "◎",
                        "○",
                        "●",
                        "◆",
                        "◇",
                        "■",
                        "□",
                        "→",
                        "⇒",
                        "♪",
                        "(",
                        ")",
                        "【",
                        "】",
                        "￥",
                        '"',
                        "＜",
                        "＞",
                        "！",
                        "？",
                        "↓",
                        "↑",
                        "←",
                        "．",
                        '"',
                        "゛",
                        "'",
                        "'",
                        '"',
                        '"',
                        "、",
                        "。",
                        "《",
                        "》",
                        "+",
                        "-",
                        "[",
                        "]",
                        "|",
                        "*",
                        "&",
                        "/",
                        ".",
                        "!",
                        "@",
                        "#",
                        "$",
                        "%",
                        "^",
                        "_",
                        "`",
                        "{",
                        "}",
                        "?",
                        ";",
                        ":",
                        ",",
                        "=",
                        "~",
                        "<",
                        ">",
                      ]}
                    />
                    <p className="small text-muted text-end mt-1">{`${
                      values.Description ? values.Description.length : 0
                    }/300`}</p>
                  </div>

                  {/* Row 7: Start Date, End Date */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="start_date"
                      />
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={isHeadquarterOption}
                      />
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="end_date"
                      />
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        // min={dayjs().format(DATE_FORMAT)}
                        disableFuture={false}
                        disabled={isHeadquarterOption}
                      />
                    </div>
                  </div>

                  {/* Row 8: Status, Remarks */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="status"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="0"
                            id="valid"
                            disabled={
                              values.RegistrationType === 0 &&
                              getUserTypeFromLocalStorage() ===
                                userTypeEnum[userTypeEnum.Store]
                            }
                          />
                          <label className="form-check-label" htmlFor="valid">
                            {formatMessage("valid")}
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="1"
                            id="invalid"
                            disabled={
                              values.RegistrationType === 0 &&
                              getUserTypeFromLocalStorage() ===
                                userTypeEnum[userTypeEnum.Store]
                            }
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            {formatMessage("invalid")}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <Label
                        className="label-style"
                        required={false}
                        label="remarks"
                      />
                      <Field
                        name="Remarks"
                        as={FormikFormTextArea}
                        className="form-control"
                        placeholder="enter_remarks"
                        type="text"
                        row={6}
                        disabled={isHeadquarterOption}
                      />
                      <p className="small text-muted text-end mt-1">{`${
                        values.Remarks ? values.Remarks.length : 0
                      }/300`}</p>
                    </div>
                  </div>
                </Modal.Body>
                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.getOptionMenuLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {isOperationPermittedRoleBase(
                    ModuleType.OptionsManagement,
                    ActionType.Edit
                  ) && (
                    <LoaderButton
                      type="submit"
                      iconName="bi-check-lg"
                      isLoading={props.getOptionMenuLoading}
                      text={props.id > -1 ? "button_update" : "button_register"}
                    />
                  )}
                  {props.id > -1 &&
                    isOperationPermittedRoleBase(
                      ModuleType.OptionsManagement,
                      ActionType.Delete
                    ) &&
                    values.RegistrationType !== 0 && (
                      <Button
                        variant="danger"
                        type="button"
                        onClick={handleDelete}
                        disabled={props.getOptionMenuLoading}
                      >
                        <i className="bi bi-trash3-fill me-1"></i>
                        <FormattedMessage id="button_delete" />
                      </Button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(observer(AddStoreOptionMenuForm));
